import type { ResponseData } from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import { utc } from '@vrplatform/utils';
import { Group, HoverCard, Stack, Text } from '@mantine/core';

type JournalEntriesRow =
  ResponseData<'get:/reports/manager-statements/detail/journal-entries'>['data'][0];

type JournalEntryIssue = JournalEntriesRow['issues'][number];

const ISSUE_LABELS: Record<JournalEntryIssue['code'], string> = {
  generalLedgerStartAt: 'Before general ledger start date',
  listingInactive: 'Listing is inactive',
  reservationGeneralLedgerStatusInactive:
    'Reservation is inactive in the general ledger',
  cancelledReservationLine: 'Reservation line was cancelled',
  inactiveAccount: 'Account is inactive',
  nonPostingAccount: 'Account is non-posting',
  unknownInactiveReason: 'Unknown inactive reason',
  transactionInactive: 'Transaction is inactive',
};

const formatIssueLabel = (issue: JournalEntryIssue) => {
  if (issue.code === 'generalLedgerStartAt') {
    const startAt = issue.context?.statementStartAt;
    if (startAt)
      return `Before general ledger start date (${utc(startAt).format(
        'MMM DD, YYYY'
      )})`;
  }

  if (issue.code === 'unknownInactiveReason') return issue.message;

  return ISSUE_LABELS[issue.code];
};

export const JournalEntryIssuesIcon = ({
  issues,
}: {
  issues: JournalEntryIssue[] | undefined;
}) => {
  if (!issues?.length) return null;

  const hasError = issues.some((issue) => issue.severity === 'error');

  return (
    <HoverCard position="top" width={260} shadow="md" withinPortal>
      <HoverCard.Target>
        <Icon
          icon="AlertTriangleIcon"
          size={16}
          color={(theme) =>
            hasError ? theme.colors.red[6] : theme.colors.orange[7]
          }
        />
      </HoverCard.Target>
      <HoverCard.Dropdown onClick={(event) => event.stopPropagation()}>
        <Stack gap={6}>
          {issues.map((issue, index) => {
            const label = formatIssueLabel(issue);
            const isError = issue.severity === 'error';

            return (
              <Group
                key={`${issue.code}-${index}`}
                gap={'xs'}
                align="center"
                wrap="nowrap"
              >
                <Icon
                  icon="AlertTriangleIcon"
                  size={14}
                  color={(theme) =>
                    isError ? theme.colors.red[6] : theme.colors.orange[7]
                  }
                />
                <Text size="xs" c={isError ? 'red' : undefined}>
                  {label}
                </Text>
              </Group>
            );
          })}
        </Stack>
      </HoverCard.Dropdown>
    </HoverCard>
  );
};
